import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';

interface AuthEvent {
  type: 'login' | 'logout' | 'register' | 'token_refresh' | 'token_expired' | 'auth_error';
  timestamp: number;
  user?: any;
  error?: string;
}

class AuthMonitor {
  private static instance: AuthMonitor;
  private events: AuthEvent[] = [];
  private maxEvents = 100;

  static getInstance(): AuthMonitor {
    if (!AuthMonitor.instance) {
      AuthMonitor.instance = new AuthMonitor();
    }
    return AuthMonitor.instance;
  }

  logEvent(event: Omit<AuthEvent, 'timestamp'>) {
    const fullEvent: AuthEvent = {
      ...event,
      timestamp: Date.now()
    };
    
    this.events.unshift(fullEvent);
    
    // Keep only the last maxEvents
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(0, this.maxEvents);
    }
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[AuthMonitor] ${event.type}:`, fullEvent);
    }
    
    // Send to analytics if configured
    this.sendToAnalytics(fullEvent);
  }

  private sendToAnalytics(event: AuthEvent) {
    // In production, you might want to send this to your analytics service
    // For now, we'll just store it locally
    try {
      const storedEvents = localStorage.getItem('auth_analytics');
      const analytics = storedEvents ? JSON.parse(storedEvents) : [];
      
      analytics.unshift({
        ...event,
        sessionId: this.getSessionId(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
      
      // Keep only last 50 events in localStorage
      const trimmedAnalytics = analytics.slice(0, 50);
      localStorage.setItem('auth_analytics', JSON.stringify(trimmedAnalytics));
    } catch (error) {
      console.warn('Failed to store auth analytics:', error);
    }
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('auth_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('auth_session_id', sessionId);
    }
    return sessionId;
  }

  getEvents(): AuthEvent[] {
    return [...this.events];
  }

  getEventsSummary() {
    const now = Date.now();
    const last24h = now - (24 * 60 * 60 * 1000);
    const lastHour = now - (60 * 60 * 1000);
    
    const events24h = this.events.filter(e => e.timestamp > last24h);
    const eventsHour = this.events.filter(e => e.timestamp > lastHour);
    
    return {
      total: this.events.length,
      last24h: events24h.length,
      lastHour: eventsHour.length,
      loginAttempts24h: events24h.filter(e => e.type === 'login').length,
      errors24h: events24h.filter(e => e.type === 'auth_error').length,
      tokenRefreshes24h: events24h.filter(e => e.type === 'token_refresh').length
    };
  }

  clearEvents() {
    this.events = [];
    localStorage.removeItem('auth_analytics');
  }
}

const AuthMonitorComponent: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const monitor = AuthMonitor.getInstance();

  useEffect(() => {
    // Monitor authentication state changes
    if (isAuthenticated && user) {
      monitor.logEvent({
        type: 'login',
        user: {
          id: user.id,
          email: user.email,
          is_verified: user.email_verified
        }
      });
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    // Monitor token expiration
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          // JWT tokens have 3 parts separated by dots
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expirationTime = payload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          const timeUntilExpiry = expirationTime - now;
          
          // Warn if token expires in less than 5 minutes
          if (timeUntilExpiry > 0 && timeUntilExpiry < 5 * 60 * 1000) {
            monitor.logEvent({
              type: 'token_expired',
              error: `Token expires in ${Math.floor(timeUntilExpiry / 1000)} seconds`
            });
          }
        } catch (error) {
          monitor.logEvent({
            type: 'auth_error',
            error: 'Failed to parse token for expiration check'
          });
        }
      }
    };

    // Check token expiration every minute
    const interval = setInterval(checkTokenExpiration, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Monitor storage events (logout from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token' && e.newValue === null && e.oldValue !== null) {
        monitor.logEvent({
          type: 'logout',
          error: 'Logged out from another tab'
        });
        enqueueSnackbar('You have been logged out from another tab', { variant: 'info' });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [enqueueSnackbar]);

  useEffect(() => {
    // Monitor network errors that might affect auth
    const handleOnline = () => {
      monitor.logEvent({
        type: 'auth_error',
        error: 'Network connection restored'
      });
    };

    const handleOffline = () => {
      monitor.logEvent({
        type: 'auth_error',
        error: 'Network connection lost'
      });
      enqueueSnackbar('Network connection lost - some features may not work', { 
        variant: 'warning',
        persist: true 
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [enqueueSnackbar]);

  // This component doesn't render anything visible
  return null;
};

export default AuthMonitorComponent;
export { AuthMonitor };